import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { selectAuth } from '@app/features/auth/auth.selectors';
import { ProtocolWorkStreamService } from '@core/services/protocol-work-stream.service';
import {
  catchError,
  concat,
  distinctUntilChanged,
  map,
  merge,
  of,
  switchMap,
} from 'rxjs';
import { ProtocolWorkActions } from './protocol-work.state';

/** Holds the private live count and releases the preceding identity's stream immediately. */
@Injectable()
export class ProtocolWorkEffects {
  private readonly actions = inject(Actions);
  private readonly store = inject(Store);
  private readonly streams = inject(ProtocolWorkStreamService);
  public readonly updates = createEffect(() =>
    this.store.select(selectAuth).pipe(
      map((auth) => ({
        key: [
          auth.authenticated,
          auth.isSecurityAdministrator,
          auth.currentUser?.id,
          ...(auth.access ?? []),
        ].join('|'),
        allowed: auth.authenticated && !auth.isSecurityAdministrator,
      })),
      distinctUntilChanged((left, right) => left.key === right.key),
      switchMap((identity) =>
        !identity.allowed
          ? of(ProtocolWorkActions.reset())
          : concat(
              of(ProtocolWorkActions.reset()),
              merge(
                of(null),
                this.actions.pipe(ofType(ProtocolWorkActions.refresh)),
              ).pipe(
                switchMap(() =>
                  this.streams
                    .observe({
                      offset: 0,
                      limit: 0,
                      sort: 'DEADLINE',
                      descending: false,
                      filter: null,
                    })
                    .pipe(
                      map((state) =>
                        ProtocolWorkActions.loaded({ count: state.count }),
                      ),
                      catchError(() => of(ProtocolWorkActions.failed())),
                    ),
                ),
              ),
            ),
      ),
    ),
  );
}
